import React, { useState } from 'react';
import { Mail, Lock, AlertCircle, Trash2, ShieldCheck, Loader2 } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.lockwaste.com/v1';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const AccountDelete: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!confirmed) {
      setError('You must confirm that you understand this action is permanent.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
        },
        body: JSON.stringify({ email: email.toLowerCase(), password }),
      });

      if (response.ok) {
        setSuccess(true);
      } else {
        const data = await response.json().catch(() => ({}));
        if (response.status === 401 || response.status === 403) {
          setError('Invalid email or password. Please try again.');
        } else {
          setError(data?.detail || 'Something went wrong. Please try again.');
        }
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex-col justify-between p-12 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-600/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-violet-600/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl pointer-events-none" />

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg overflow-hidden shadow-lg shadow-indigo-500/30">
              <img src="/icon.png" alt="Lockwaste" className="w-full h-full object-cover" />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">LOCKWASTE</span>
          </div>
        </div>

        <div className="relative z-10 space-y-8">
          <div>
            <h1 className="text-4xl font-extrabold text-white leading-tight mb-4">
              Account<br />deletion
            </h1>
            <p className="text-slate-400 text-base leading-relaxed max-w-xs">
              Permanently remove your account and all associated data from our platform.
            </p>
          </div>
          <div className="space-y-4">
            {[
              { icon: ShieldCheck, text: 'All your bins and groups will be removed' },
              { icon: ShieldCheck, text: 'Access logs and events will be deleted' },
              { icon: ShieldCheck, text: 'Your subscription will be cancelled' },
            ].map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-indigo-400" />
                </div>
                <span className="text-slate-300 text-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-slate-500 text-sm">
          © {new Date().getFullYear()} Lockwaste Smart Solutions
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-9 h-9 rounded-lg overflow-hidden shadow">
              <img src="/icon.png" alt="Lockwaste" className="w-full h-full object-cover" />
            </div>
            <span className="font-bold text-xl text-gray-900 tracking-tight">LOCKWASTE</span>
          </div>

          {success ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center space-y-4">
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto">
                <ShieldCheck className="w-7 h-7 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Account deleted</h2>
              <p className="text-gray-500 text-sm">
                Your account and all associated data have been permanently removed from our systems.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <div className="mb-8">
                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                  <Trash2 className="w-6 h-6 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Delete your account</h2>
                <p className="text-gray-500 text-sm">
                  This action is <span className="font-semibold text-red-500">permanent and irreversible</span>. Enter your credentials to confirm.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">Email address</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                    />
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={e => setConfirmed(e.target.checked)}
                    className="mt-0.5 accent-red-500"
                  />
                  <span className="text-sm text-gray-600">
                    I understand that deleting my account is permanent. All my bins, events, and data will be removed and cannot be recovered.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={isLoading || !confirmed}
                  className="w-full flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors"
                >
                  {isLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Deleting account…</>
                  ) : (
                    <><Trash2 className="w-4 h-4" /> Delete my account permanently</>
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-xs text-gray-400">
                Changed your mind?{' '}
                <a href="/login" className="text-indigo-500 hover:underline font-medium">
                  Go back to login
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountDelete;
